import React, { useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Spinner,
  Button,
  FormGroup,
  Label,
  Input,
  ModalFooter,
  Modal,
  ModalHeader,
  ModalBody,
  UncontrolledTooltip
} from 'reactstrap';
import { InfoCircle, Reply } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import SimpleHeader from '../../components/Headers/SimpleHeader';
import client from '../../utils/client';
import { isSuperAdmin } from '../../utils/helpers';

const isUserSuperAdmin = isSuperAdmin();

const defaultYear = new Date().getFullYear();
const nrOfYears = defaultYear - 2009 + 1;
const options = Array.from({ length: nrOfYears }, (_, i) => defaultYear - i).map((i) => (
  <option key={i} value={i}>
    {i}
  </option>
));

const Comments = () => {
  const { t } = useTranslation();

  const [inProgress, setInProgress] = useState(null);
  const [comments, setComments] = useState(null);
  const [year, setYear] = useState(defaultYear);
  const [answerId, setAnswerId] = useState(null);
  const [answerText, setAnswerText] = useState(null);

  useEffect(() => {
    document.body.classList.add('visits');
    return () => document.body.classList.remove('visits');
  }, []);

  useEffect(() => {
    (async () => {
      setInProgress(true);
      const { data } = await client(`admin-gc/surveys/comments?limit=1000&year=${year}`);
      setComments(data);
      setInProgress(null);
    })();
  }, [year]);

  const onApprovedChange = async (commentId, e) => {
    setInProgress(commentId);
    const approved = e.target.checked;
    const { error } = await client(`admin-gc/surveys/comments/${commentId}`, {
      body: { approved }
    });
    if (!error) {
      setComments(comments.map((c) => (c.id === commentId ? { ...c, approved } : { ...c })));
    }
    setInProgress(null);
  };

  const onAnswerChange = ({ target }) => setAnswerText(target.value);

  const onAnswerClick = async () => {
    if (answerText) {
      setInProgress(answerId);

      const { error } = await client(`admin-gc/surveys/comments/${answerId}`, {
        body: { clubAnswer: answerText }
      });
      if (!error) {
        setComments(
          comments.map((c) => (c.id === answerId ? { ...c, clubAnswer: answerText } : { ...c }))
        );
      }
      setInProgress(null);
      setAnswerText(null);
      setAnswerId(null);
    }
  };
  // console.log('isUserUsperAdmin', isUserSuperAdmin);

  return (
    <>
      <SimpleHeader name={t('statistic-comments.title', 'Kommentarer')} />

      <Container className="mt--6" fluid>
        <Row>
          <Col xs="12">
            <Row>
              <Col>
                <Card>
                  <CardHeader>
                    <h3 className="mb-0">
                      {t('statistic-comments.sub-title', 'Vad tycker era besökare')}
                    </h3>
                  </CardHeader>
                  <CardBody>
                    <FormGroup>
                      <Input type="select" onChange={(e) => setYear(e.target.value)}>
                        {options}
                      </Input>
                    </FormGroup>
                    {typeof inProgress === 'boolean' && <Spinner />}
                    <Table responsive>
                      <thead className="thead-light">
                        <tr>
                          <th>{t('default.date')}</th>
                          <th>{t('statistic-comments.user', 'Användare')}</th>
                          <th>{t('statistic-overview.grade')}</th>
                          <th>{t('statistic-comments.comment', 'Kommentar')}</th>
                          <th>{t('default.reply', 'Svara')}</th>
                          {isUserSuperAdmin && (
                            <>
                              <th>{t('statistic-comments.approved', 'Godkänd')}</th>
                              <th>
                                {t('statistic-comments.complaint-or-question', 'Klagomål/Fråga')}
                              </th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {comments?.map((c) => (
                          <tr key={c.id}>
                            <td>{c.date}</td>
                            <td>
                              <div>
                                <p
                                  href="#"
                                  id={`comment-${c.id}`}
                                  style={{ color: '#2dce89', cursor: 'pointer' }}
                                >
                                  {c.firstname} <InfoCircle size={12} />
                                </p>
                                <UncontrolledTooltip placement="right" target={`comment-${c.id}`}>
                                  {`${c.firstname} ${c.lastname}\n${c.email}`}
                                </UncontrolledTooltip>
                              </div>
                            </td>
                            <td>{c.overallGrade}</td>

                            <td style={{ whiteSpace: 'normal' }}>
                              {c.comment}
                              {c.translated ? (
                                <span className="mt-3 d-block">
                                  <b>{t('default.translation', 'Översättning')}</b>: {c.translated}
                                </span>
                              ) : (
                                ''
                              )}
                              {c.clubAnswer && (
                                <p className="small alert-secondary p-2">
                                  {t('default.answer', 'Svar')}: {c.clubAnswer}
                                </p>
                              )}
                            </td>
                            <td>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setAnswerText(c.clubAnswer);
                                  setAnswerId(c.id);
                                }}
                              >
                                <Reply />
                              </Button>
                            </td>
                            {isUserSuperAdmin && (
                              <>
                                <td>
                                  {inProgress === c.id ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <FormGroup check>
                                      <Label check>
                                        <Input
                                          name="onlyInvoice"
                                          type="checkbox"
                                          checked={c.approved}
                                          onChange={(e) => onApprovedChange(c.id, e)}
                                        />
                                        {c.approvedBy}
                                      </Label>
                                    </FormGroup>
                                  )}
                                </td>

                                <td>
                                  {c.complaints ? 'Ja' : ''}
                                  {c.questions ? ' / Ja' : ''}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {answerId && (
        <Modal isOpen centered>
          {/* <form onSubmit={handleSubmitSearch(onSubmitSearch)}> */}
          <ModalHeader>{t('statistic-comments.answer', 'Svara kunden')}</ModalHeader>
          <ModalBody className="pt-0">
            <FormGroup className="mb-2">
              <Input name="answer" type="textarea" value={answerText} onChange={onAnswerChange} />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button type="submit" color="success" onClick={onAnswerClick} disabled={!!inProgress}>
              {inProgress && <Spinner size="sm" className="mr-2" />}
              {t('default.reply')}
            </Button>
            <Button color="danger" onClick={() => setAnswerId(null)}>
              {t('default.close')}
            </Button>
          </ModalFooter>
          {/* </form> */}
        </Modal>
      )}
    </>
  );
};

export default Comments;
